'use client'

import Loader from '@/components/loader'
import Button from '@/components/ui/button'
import PageTransition from '@/components/ui/page-transition'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Text } from '@/components/ui/text'
import { getProviderGamesFn } from '@/services'
import { css } from '@/styled-system/css'
import { Flex } from '@/styled-system/jsx'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useTranslations } from 'next-intl'
import { useSearch } from './use-search'

import GameList from '../casino/game/game-list'
import NoResults from './search-no-results'
import RecentSearchs from './search-recent-searchs'

const ITEMS_PER_PAGE = 20

export default function SearchResults() {
  const t = useTranslations()
  const { addToRecents, query, isTyping, inputValue } = useSearch()

  const { data, fetchNextPage, isFetchingNextPage, hasNextPage, status } =
    useInfiniteQuery(
      ['search-games', query],
      async ({ pageParam = 0 }) => {
        const result = await getProviderGamesFn({
          start: pageParam * ITEMS_PER_PAGE,
          end: ITEMS_PER_PAGE,
          providers: undefined,
          search: query,
        })

        if (result.games.length > 0 && pageParam === 0) {
          addToRecents(query)
        }

        return result
      },
      {
        refetchOnWindowFocus: false,
        getNextPageParam: (lastPage, pages) => {
          return lastPage.games.length < ITEMS_PER_PAGE
            ? undefined
            : pages.length
        },
      }
    )

  const games = data ? data.pages.flatMap((value) => value.games) : []
  const total = data ? data.pages[0].total : 0

  if (inputValue.length < 3) {
    return (
      <Flex direction='column' gap='5'>
        <Text
          as='span'
          color='#CEBFD9'
          textStyle='sm'
          textAlign='center'
          mt='1'
        >
          Search requires at least 3 characters.
        </Text>
        <RecentSearchs />
      </Flex>
    )
  }

  return (
    <>
      {status === 'loading' || isTyping ? (
        <Flex align='center' justify='center' h={{ base: 'full', md: '550px' }}>
          <Loader />
        </Flex>
      ) : games.length > 0 ? (
        <ScrollArea h={{ base: 'auto', md: '550px' }}>
          <PageTransition
            className={css({
              display: 'flex',
              flexDir: 'column',
              h: { base: 'auto', md: '550px' },
            })}
          >
            <Flex direction='column' justify='space-between' grow={1} pt='4'>
              <GameList games={games} />

              <Flex direction='column' gap='3' mt='4'>
                {hasNextPage ? (
                  <Button
                    variant='tertiary'
                    size='sm'
                    mt={4}
                    h={12}
                    w='full'
                    loading={isFetchingNextPage}
                    onClick={() => {
                      fetchNextPage()
                    }}
                  >
                    {t('load-more')}
                  </Button>
                ) : null}

                <Text as='span' textAlign='center' textStyle='sm'>
                  Displaying <Text as='b'>{games.length}</Text> of{' '}
                  <Text as='b'>{total}</Text> games
                </Text>
              </Flex>
            </Flex>
          </PageTransition>
        </ScrollArea>
      ) : (
        <Flex direction='column' gap='5'>
          <NoResults />
          <RecentSearchs />
        </Flex>
      )}
    </>
  )
}
